/**
 * O ato Reclassificar do item da campanha (D-190, P-22 a P-25; DS-647).
 *
 * ## O que é
 *
 * O Pedido guarda a classificação COMERCIAL do item (o que foi vendido); a campanha guarda a OPERACIONAL
 * (o que está sendo veiculado). As duas nascem iguais e só a operacional muda aqui, gravada na hora, com
 * trilha. Reclassificar não reescreve o Pedido, outras campanhas, apurações nem a meta efetiva (D-190).
 *
 * ## As validações (P-23)
 *
 * Produto e sub-produto existem e estão ativos, o sub-produto é do produto, e a forma de venda RESOLVIDA
 * (a do sub-produto, ou a do produto) tem o comportamento `com_veiculacao`: campanha veicula, e um item
 * sem veiculação (Setup, Assinatura) não é item de campanha.
 *
 * ## A meta de custo de mídia (P-24)
 *
 * A meta do item é a dele (percentual ou valor) ou, sem nenhuma, o padrão do PRODUTO, lido ao vivo. Trocar
 * o produto de um item que herdava o padrão trocaria a meta em silêncio; por isso a troca grava no item o
 * percentual que valia, e a meta efetiva fica igual ao centavo. Mudar só o sub-produto não toca custo.
 * Adotar o padrão do produto novo é escolha explícita, de quem edita os custos, e não existe na campanha
 * encerrada (P-25: lá reclassificar é só etiqueta) nem para produto sem padrão (não há o que adotar, e
 * limpar a meta deixaria o item sem meta e a margem indisponível, P-13). Item que não tinha meta nenhuma
 * (o produto antigo sem padrão) e vai para produto COM padrão mudaria a meta de "ausente" para o padrão
 * novo: isso também só acontece pela escolha explícita.
 *
 * ## O status (P-25)
 *
 * Reclassifica-se de Programada a Concluída (a Pausada inclusive); na Concluída é só etiqueta, sem adotar a meta
 * padrão; na Cancelada, nunca. A régua é a da tabela única da edição por status (§5.5, P-10, `status.ts`).
 *
 * Aqui só mora a decisão, sobre fatos que quem chama já leu. Quem lê o catálogo, trava, grava e registra a
 * trilha é a API.
 */
import { EDICAO_POR_STATUS, permiteNoStatus, rotuloDoStatusNaFrase } from './status.js'

/** Os estados em que o item pode ser reclassificado (P-25): a linha "Reclassificar" da tabela do §5.5. */
export const STATUS_QUE_RECLASSIFICAM = EDICAO_POR_STATUS.reclassificar

export function podeReclassificarNoStatus(status: string): boolean {
  return permiteNoStatus('reclassificar', status)
}

/** Adotar a meta padrão do produto novo é escolha de quem edita os custos, e nunca na campanha concluída (§5.5). */
export function podeAdotarMetaPadraoNoStatus(status: string): boolean {
  return permiteNoStatus('adotar_meta_padrao', status)
}

/**
 * Só item com veiculação entra na campanha (P-23): a forma de venda RESOLVIDA (a do sub-produto, ou a do produto) tem
 * o comportamento `com_veiculacao`. A mesma régua decide o Reclassificar e as opções do catálogo dele.
 */
export function vendeComVeiculacao(comportamentoDaForma: string | null): boolean {
  return comportamentoDaForma === 'com_veiculacao'
}

export interface ProdutoDaReclassificacao {
  readonly id: string
  readonly ativo: boolean
  /** O padrão de meta de custo de mídia do produto, em fração (`0.07` é 7%); `null` sem padrão. */
  readonly custoMidiaPctDefault: string | null
}

export interface SubprodutoDaReclassificacao {
  readonly id: string
  readonly produtoId: string
  readonly ativo: boolean
}

export interface ItemDaReclassificacao {
  readonly produtoId: string
  readonly subprodutoId: string | null
  readonly custoMidiaPct: string | null
  readonly custoMidiaReais: string | null
}

export interface FatosDaReclassificacao {
  readonly status: string
  readonly item: ItemDaReclassificacao
  /** O padrão de meta do produto ATUAL do item, como vale hoje. */
  readonly padraoDoProdutoAtual: string | null
  readonly produtoIdPedido: string
  /** O produto pedido, lido do catálogo; `null` quando ele não existe. */
  readonly produtoNovo: ProdutoDaReclassificacao | null
  readonly subprodutoIdPedido: string | null
  /** O sub-produto pedido, lido do catálogo; `null` quando não foi pedido ou não existe. */
  readonly subprodutoNovo: SubprodutoDaReclassificacao | null
  /** O comportamento da forma de venda resolvida do par pedido; `null` sem forma. */
  readonly comportamentoDaForma: string | null
  readonly adotarMetaPadrao: boolean
  readonly podeEditarCustos: boolean
}

export type RecusaDaReclassificacao =
  | 'campanha_cancelada'
  | 'status_nao_reclassifica'
  | 'produto_inexistente'
  | 'produto_inativo'
  | 'subproduto_inexistente'
  | 'subproduto_de_outro_produto'
  | 'subproduto_inativo'
  | 'forma_sem_veiculacao'
  | 'adocao_sem_troca_de_produto'
  | 'adocao_em_encerrada'
  | 'adocao_exige_custos'
  | 'produto_sem_meta_padrao'
  | 'meta_mudaria_sem_escolha'

export type CampoDaReclassificacao = 'status' | 'produtoId' | 'subprodutoId' | 'adotarMetaPadrao'

export type MetaDaReclassificacao =
  /** As colunas de custo do item não mudam. */
  | { readonly tipo: 'intocada' }
  /** P-24: o item herdava o padrão do produto antigo; grava-se esse percentual no item. */
  | { readonly tipo: 'herdada_gravada'; readonly custoMidiaPct: string }
  /** A escolha explícita: o item volta a herdar, agora do produto novo (percentual e valor em branco). */
  | { readonly tipo: 'padrao_novo' }

export type ResultadoDaReclassificacao =
  | { readonly tipo: 'recusada'; readonly recusa: RecusaDaReclassificacao; readonly campo: CampoDaReclassificacao }
  /** O par pedido é o gravado, sem escolha de meta: nada a gravar. */
  | { readonly tipo: 'sem_mudanca' }
  | {
      readonly tipo: 'reclassificar'
      readonly produtoMudou: boolean
      readonly subprodutoMudou: boolean
      readonly meta: MetaDaReclassificacao
    }

const MENSAGENS: Readonly<Record<RecusaDaReclassificacao, string>> = {
  campanha_cancelada: `Campanha ${rotuloDoStatusNaFrase('Cancelada')} não é reclassificada.`,
  status_nao_reclassifica: 'Esta campanha não pode ser reclassificada no estado atual.',
  produto_inexistente: 'O produto escolhido não existe. Escolha um produto do catálogo.',
  produto_inativo: 'O produto escolhido está inativo. Escolha um produto ativo.',
  subproduto_inexistente: 'O sub-produto escolhido não existe. Escolha um sub-produto do produto.',
  subproduto_de_outro_produto: 'O sub-produto escolhido não é do produto escolhido. Escolha um sub-produto dele.',
  subproduto_inativo: 'O sub-produto escolhido está inativo. Escolha um sub-produto ativo.',
  forma_sem_veiculacao:
    'A forma de venda escolhida não é com veiculação, e só item com veiculação entra na campanha.',
  adocao_sem_troca_de_produto: 'A meta padrão de custo de mídia só é adotada quando o produto muda.',
  adocao_em_encerrada: `Na campanha ${rotuloDoStatusNaFrase('Encerrada')}, reclassificar não muda a meta de custo de mídia.`,
  adocao_exige_custos: 'Adotar a meta padrão do produto novo exige permissão para editar os custos da campanha.',
  produto_sem_meta_padrao:
    'O produto escolhido não tem meta padrão de custo de mídia para adotar. Reclassifique sem adotar a meta padrão.',
  meta_mudaria_sem_escolha:
    'O item não tinha meta de custo de mídia, e o produto novo tem meta padrão. ' +
    'Para reclassificar, escolha adotar a meta padrão do produto novo.',
}

export function mensagemDaRecusaDeReclassificacao(recusa: RecusaDaReclassificacao): string {
  return MENSAGENS[recusa]
}

function mesmoId(a: string | null, b: string | null): boolean {
  return a === null || b === null ? a === b : a.toLowerCase() === b.toLowerCase()
}

function recusa(motivo: RecusaDaReclassificacao, campo: CampoDaReclassificacao): ResultadoDaReclassificacao {
  return { tipo: 'recusada', recusa: motivo, campo }
}

/**
 * P-23, a régua do catálogo para uma classificação operacional nova: o produto existe e está ativo, o sub-produto (quando
 * pedido) existe, é dele e está ativo, e a forma de venda resolvida é com veiculação. É a mesma do Reclassificar e da
 * linha nova da composição que vem com classificação própria (D-193 (2)). Aceita, devolve o produto lido.
 */
export function recusaDoCatalogoDaClassificacao(f: Pick<
  FatosDaReclassificacao, 'produtoNovo' | 'subprodutoIdPedido' | 'subprodutoNovo' | 'comportamentoDaForma'
>):
  | { readonly tipo: 'recusada'; readonly recusa: RecusaDaReclassificacao; readonly campo: CampoDaReclassificacao }
  | { readonly tipo: 'aceita'; readonly produto: ProdutoDaReclassificacao } {
  if (f.produtoNovo === null) return { tipo: 'recusada', recusa: 'produto_inexistente', campo: 'produtoId' }
  if (!f.produtoNovo.ativo) return { tipo: 'recusada', recusa: 'produto_inativo', campo: 'produtoId' }
  if (f.subprodutoIdPedido !== null) {
    if (f.subprodutoNovo === null) return { tipo: 'recusada', recusa: 'subproduto_inexistente', campo: 'subprodutoId' }
    if (!mesmoId(f.subprodutoNovo.produtoId, f.produtoNovo.id)) {
      return { tipo: 'recusada', recusa: 'subproduto_de_outro_produto', campo: 'subprodutoId' }
    }
    if (!f.subprodutoNovo.ativo) return { tipo: 'recusada', recusa: 'subproduto_inativo', campo: 'subprodutoId' }
  }
  if (!vendeComVeiculacao(f.comportamentoDaForma)) {
    return { tipo: 'recusada', recusa: 'forma_sem_veiculacao', campo: f.subprodutoIdPedido === null ? 'produtoId' : 'subprodutoId' }
  }
  return { tipo: 'aceita', produto: f.produtoNovo }
}

export function decidirReclassificacao(f: FatosDaReclassificacao): ResultadoDaReclassificacao {
  if (f.status === 'Cancelada') return recusa('campanha_cancelada', 'status')
  if (!podeReclassificarNoStatus(f.status)) return recusa('status_nao_reclassifica', 'status')

  const produtoMudou = !mesmoId(f.item.produtoId, f.produtoIdPedido)
  const subprodutoMudou = !mesmoId(f.item.subprodutoId, f.subprodutoIdPedido)
  if (f.adotarMetaPadrao && !produtoMudou) return recusa('adocao_sem_troca_de_produto', 'adotarMetaPadrao')
  if (!produtoMudou && !subprodutoMudou) return { tipo: 'sem_mudanca' }

  // P-23: o catálogo.
  const doCatalogo = recusaDoCatalogoDaClassificacao(f)
  if (doCatalogo.tipo === 'recusada') return recusa(doCatalogo.recusa, doCatalogo.campo)
  const produtoNovo = doCatalogo.produto

  // P-24: a meta.
  if (f.adotarMetaPadrao) {
    if (!podeAdotarMetaPadraoNoStatus(f.status)) return recusa('adocao_em_encerrada', 'adotarMetaPadrao')
    if (!f.podeEditarCustos) return recusa('adocao_exige_custos', 'adotarMetaPadrao')
    // Sem padrão no produto novo, "adotar" só apagaria a meta do item (DS-647(c)).
    if (produtoNovo.custoMidiaPctDefault === null) return recusa('produto_sem_meta_padrao', 'adotarMetaPadrao')
    return { tipo: 'reclassificar', produtoMudou, subprodutoMudou, meta: { tipo: 'padrao_novo' } }
  }
  const intocada = { tipo: 'reclassificar', produtoMudou, subprodutoMudou, meta: { tipo: 'intocada' } } as const
  if (!produtoMudou) return intocada
  const temMetaPropria = f.item.custoMidiaPct !== null || f.item.custoMidiaReais !== null
  if (temMetaPropria) return intocada
  if (f.padraoDoProdutoAtual !== null) {
    return {
      tipo: 'reclassificar', produtoMudou, subprodutoMudou,
      meta: { tipo: 'herdada_gravada', custoMidiaPct: f.padraoDoProdutoAtual },
    }
  }
  // Sem meta antes: continua sem meta só se o produto novo também não tiver padrão.
  if (produtoNovo.custoMidiaPctDefault === null) return intocada
  return recusa('meta_mudaria_sem_escolha', 'adotarMetaPadrao')
}
